import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as Models from '../models/api.models';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Log API
  getEvents(region?: Models.Region, max: number = 50): Observable<any> {
    let params = new HttpParams().set('max', max.toString());
    if (region) params = params.set('region', region);
    return this.http.get(`${this.baseUrl}/api/logs/events`, { params });
  }

  getAdminEvents(region?: Models.Region, max: number = 50): Observable<any> {
    let params = new HttpParams().set('max', max.toString());
    if (region) params = params.set('region', region);
    return this.http.get(`${this.baseUrl}/api/logs/admin-events`, { params });
  }

  // Login API
  loginByPassword(request: Models.LoginRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/login/by-password`, request);
  }

  loginBySmsOtp(request: Models.LoginRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/login/by-smsotp`, request);
  }

  verifySmsOtp(request: Models.VerifyOtpRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/login/verify-smsotp`, request);
  }

  completeLogin(request: Models.CompleteLoginRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/login/complete`, request);
  }

  getLoggedInDetail(): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/login/logedin-detail`);
  }

  refresh(request: Models.RefreshRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/login/refresh`, request);
  }

  logout(request: Models.RefreshRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/login/logout`, request);
  }

  // Master API
  getClients(region?: Models.Region): Observable<any> {
    let params = new HttpParams();
    if (region) params = params.set('region', region);
    return this.http.get(`${this.baseUrl}/api/clients`, { params });
  }

  getRolesByClientMaster(client: Models.ProjectType, region?: Models.Region): Observable<any> {
    let params = new HttpParams();
    if (region) params = params.set('region', region);
    return this.http.get(`${this.baseUrl}/api/roles/${client}`, { params });
  }

  getUsersByClientMaster(client: Models.ProjectType, region?: Models.Region): Observable<any> {
    let params = new HttpParams();
    if (region) params = params.set('region', region);
    return this.http.get(`${this.baseUrl}/api/users/${client}`, { params });
  }

  // Reset API
  startReset(request: Models.ResetStartRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/reset/start`, request);
  }

  verifyReset(request: Models.ResetVerifyRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/reset/verify`, request);
  }

  applyReset(request: Models.ResetApplyRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/reset/apply`, request);
  }

  // Roles API
  createRole(request: Models.CreateRoleRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/roles`, request);
  }

  getRolesByClient(client: Models.ProjectType, region?: Models.Region): Observable<any> {
    let params = new HttpParams();
    if (region) params = params.set('region', region);
    return this.http.get(`${this.baseUrl}/api/roles/client/${client}`, { params });
  }

  assignRoles(userId: string, request: Models.AssignRolesRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/users/${userId}/roles`, request);
  }

  getUserRoles(userId: string, client?: Models.ProjectType): Observable<any> {
    let params = new HttpParams();
    if (client) params = params.set('client', client);
    return this.http.get(`${this.baseUrl}/api/users/${userId}/roles`, { params });
  }

  deleteRoles(userId: string, request: Models.AssignRolesRequest): Observable<any> {
    return this.http.request('delete', `${this.baseUrl}/api/users/${userId}/roles`, { body: request });
  }

  getUsersByClient(client: Models.ProjectType, region?: Models.Region): Observable<any> {
    let params = new HttpParams();
    if (region) params = params.set('region', region);
    return this.http.get(`${this.baseUrl}/api/users/client/${client}`, { params });
  }

  // User API
  addUser(request: Models.CreateUserRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/user/add-user`, request);
  }

  changePassword(request: Models.ChangePasswordRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/user/change-password`, request);
  }

  verifyEmail(request: Models.VerifyEmailRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/user/verify-email`, request);
  }

  resendVerification(request: Models.ResendVerificationRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/user/resend-verification`, request);
  }

  listUsers(region?: Models.Region): Observable<any> {
    let params = new HttpParams();
    if (region) params = params.set('region', region);
    return this.http.get(`${this.baseUrl}/api/user/list-users`, { params });
  }

  getUser(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/user/user/${id}`);
  }

  setUserStatus(request: Models.SetUserRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/user/set-status`, request);
  }

  updateUser(id: string, formData: FormData): Observable<any> {
    return this.http.put(`${this.baseUrl}/api/user/${id}/update`, formData);
  }

  assignClientRole(request: Models.AssignClientRoleRequestDTO): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/user/assign-client-role`, request);
  }

  deleteUser(userId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/api/user/delete-user/${userId}`);
  }
}
